import React from "react";
import {
  FiCreditCard,
  FiEdit2,
  FiFileText,
  FiMessageCircle,
  FiPhone,
  FiTrash2,
} from "react-icons/fi";
import StatusBadge from "../call-center/listing/StatusBadge";
import {
  LISTING_ICON_ACTION_CLASS,
  LISTING_ICON_ACTION_DANGER_CLASS,
  LISTING_TABLE_HEAD_CLASS,
  LISTING_TABLE_HEADER_CELL_CLASS,
  LISTING_TABLE_ROW_CLASS,
} from "../call-center/listing/listingStyles";
import {
  formatOrganizationProvider,
  getOrganizationDisplayCode,
  getOrganizationInitials,
  isRetiredOrganizationCode,
} from "./organizationManagementUtils";

const FeatureBadge = ({ enabled, t }) => (
  <StatusBadge
    status={enabled === false ? "CANCELLED" : "ACTIVE"}
    label={enabled === false ? t("ccDisabled") : t("ccEnabled")}
  />
);

const ChannelPill = ({ active, label }) => (
  <span
    className={`inline-flex rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
      active
        ? "bg-sky-50 text-sky-800 ring-1 ring-sky-200/90"
        : "bg-slate-100 text-slate-400 ring-1 ring-slate-200/80"
    }`}
  >
    {label}
  </span>
);

const CallingFeatureCell = ({ org, t }) => {
  if (org.callingEnabled === false) {
    return <FeatureBadge enabled={false} t={t} />;
  }
  return (
    <div className="flex flex-col items-start gap-1.5">
      <FeatureBadge enabled t={t} />
      <div className="flex flex-wrap gap-1">
        <ChannelPill active={org.webCallingEnabled !== false} label={t("ccOrgChannelsWeb")} />
        <ChannelPill active={org.desktopCallingEnabled !== false} label={t("ccOrgChannelsDesktop")} />
      </div>
    </div>
  );
};

const ConfigActionButton = ({ label, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={LISTING_ICON_ACTION_CLASS}
    title={label}
    aria-label={label}
  >
    {children}
  </button>
);

const OrganizationListTable = ({
  organizations,
  t,
  formatTimestamp,
  onEdit,
  onDeactivate,
  onOpenTemplate,
  onOpenPayment,
  onOpenTelephony,
  onOpenWhatsApp,
}) => (
  <>
    <p className="border-b border-slate-100 px-4 py-2.5 text-xs text-slate-500 sm:px-5">{t("ccOrgActionsLegend")}</p>
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className={LISTING_TABLE_HEAD_CLASS}>
          <tr>
            <th className={LISTING_TABLE_HEADER_CELL_CLASS}>{t("ccOrgTableOrganization")}</th>
            <th className={LISTING_TABLE_HEADER_CELL_CLASS}>{t("ccStatus")}</th>
            <th className={LISTING_TABLE_HEADER_CELL_CLASS}>{t("ccOrgTelephony")}</th>
            <th className={LISTING_TABLE_HEADER_CELL_CLASS}>{t("ccOrgCalling")}</th>
            <th className={LISTING_TABLE_HEADER_CELL_CLASS}>{t("ccOrgAiCalling")}</th>
            <th className={LISTING_TABLE_HEADER_CELL_CLASS}>{t("ccOrgCreatedAt")}</th>
            <th className={`${LISTING_TABLE_HEADER_CELL_CLASS} text-right min-w-[220px]`}>{t("ccActions")}</th>
          </tr>
        </thead>
        <tbody>
          {organizations.map((org) => {
            const displayCode = getOrganizationDisplayCode(org.code);
            const retiredCode = isRetiredOrganizationCode(org.code);
            const initials = getOrganizationInitials(org.name, displayCode);

            return (
              <tr
                key={org.id}
                className={`${LISTING_TABLE_ROW_CLASS} ${!org.active ? "bg-slate-50/70" : ""}`}
              >
                <td className="px-4 py-3.5 sm:px-5">
                  <div className="flex items-center gap-3 min-w-[200px]">
                    <span
                      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-xs font-bold ${
                        org.active
                          ? "bg-[#2B7FFF]/10 text-[#2B7FFF] ring-1 ring-[#2B7FFF]/20"
                          : "bg-slate-100 text-slate-500 ring-1 ring-slate-200"
                      }`}
                      aria-hidden="true"
                    >
                      {initials}
                    </span>
                    <div className="min-w-0">
                      <div className="truncate font-semibold text-slate-900">{org.name}</div>
                      <div className="mt-0.5 font-mono text-[11px] font-medium text-slate-500">{displayCode}</div>
                      {retiredCode ? (
                        <div className="mt-0.5 truncate text-[10px] text-slate-400" title={org.code}>
                          {t("ccOrgRetiredCode")}: {org.code}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3.5">
                  <StatusBadge
                    status={org.active ? "ACTIVE" : "CANCELLED"}
                    label={org.active ? t("ccActive") : t("ccInactive")}
                  />
                </td>
                <td className="px-4 py-3.5">
                  <span className="inline-flex max-w-[140px] truncate rounded-lg bg-slate-100 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-700">
                    {formatOrganizationProvider(org.telephonyProvider, t)}
                  </span>
                </td>
                <td className="px-4 py-3.5">
                  <CallingFeatureCell org={org} t={t} />
                </td>
                <td className="px-4 py-3.5">
                  <FeatureBadge enabled={org.aiCallingEnabled} t={t} />
                </td>
                <td className="px-4 py-3.5 whitespace-nowrap text-slate-500">{formatTimestamp(org.createdAt)}</td>
                <td className="px-4 py-3.5">
                  <div className="flex items-center justify-end gap-1">
                    <div className="inline-flex items-center gap-0.5 rounded-xl border border-slate-200 bg-slate-50/80 p-0.5">
                      <ConfigActionButton label={t("ccOrgPaymentProofTemplate")} onClick={() => onOpenTemplate(org)}>
                        <FiFileText className="h-4 w-4" />
                      </ConfigActionButton>
                      <ConfigActionButton label={t("ccOrgPaymentConfirmation")} onClick={() => onOpenPayment(org)}>
                        <FiCreditCard className="h-4 w-4" />
                      </ConfigActionButton>
                      <ConfigActionButton label={t("ccOrgTelephony")} onClick={() => onOpenTelephony(org)}>
                        <FiPhone className="h-4 w-4" />
                      </ConfigActionButton>
                      <ConfigActionButton label={t("ccOrgWhatsApp")} onClick={() => onOpenWhatsApp(org)}>
                        <FiMessageCircle className="h-4 w-4" />
                      </ConfigActionButton>
                    </div>
                    <ConfigActionButton label={t("ccEdit")} onClick={() => onEdit(org)}>
                      <FiEdit2 className="h-4 w-4" />
                    </ConfigActionButton>
                    {org.active && org.id !== 1 ? (
                      <button
                        type="button"
                        onClick={() => onDeactivate(org)}
                        className={LISTING_ICON_ACTION_DANGER_CLASS}
                        title={t("ccOrgDeactivate")}
                        aria-label={t("ccOrgDeactivate")}
                      >
                        <FiTrash2 className="h-4 w-4" />
                      </button>
                    ) : org.id === 1 ? (
                      <span
                        className="inline-flex h-9 w-9 items-center justify-center text-slate-300"
                        title={t("ccOrgDefaultProtected")}
                        aria-hidden="true"
                      />
                    ) : null}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </>
);

export default OrganizationListTable;
